/**
 * @fileoverview Componente de filtros de categorías con selección única
 * @module CategoryFilter
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.0.0
 */
import React, {
  useState, useEffect, useCallback, memo, forwardRef, useImperativeHandle
} from 'react';
import { useTranslation } from 'react-i18next';
import useCategoriesApi from '../../../../hooks/useCategoriesApi';
import LoadingMC from '../../../components/LoadingMC';
import CheckBoxMC from '../../../components/CheckBoxMC';

/**
 * @typedef {Object} Category
 * @description Categoría de productos con checkbox de selección
 * @property {string} web_id - ID web de la categoría
 * @property {string} name - Nombre de la categoría
 * @property {boolean} selected - Si está seleccionada o no
 */

/**
 * Componente Molecule para filtrar productos por categorías.
 *
 * Implementa el patrón **Molecule** de Atomic Design con funcionalidad
 * de selección única de categorías mediante checkboxes.
 *
 * @component CategoryFilter
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Lista de categorías con checkboxes
 * - Selección única de categorías
 * - Botones de guardar y limpiar filtros
 * - Estado local sincronizado con categorías seleccionadas
 * - Diseño responsivo con Tailwind CSS
 *
 * **Atomic Design:**
 * - **Nivel**: Molecule
 * - **Composición**: Checkboxes y botones de acción
 * - **Dependencias**: useCategoriesApi hook
 *
 * @param {Object} props - Propiedades del componente
 * @param {Array<string>} props.selectedCategoryIds - IDs de categorías seleccionadas
 *
 * @returns {JSX.Element} Elemento JSX renderizado con filtros de categorías
 *
 * @example
 * ```jsx
 * <CategoryFilter
 *   selectedCategoryIds={['cat1']}
 * />
 * ```
 */
const CategoryFilter = memo(forwardRef(({
  selectedCategoryIds = []
}, ref) => {
  const { t } = useTranslation('Products');

  // Estado local para categorías seleccionadas temporalmente
  const [localSelectedIds, setLocalSelectedIds] = useState(selectedCategoryIds);

  /**
   * Expone métodos para acceso desde componente padre.
   */
  useImperativeHandle(ref, () => ({
    getLocalSelectedIds: () => localSelectedIds,
    clearLocalSelectedIds: () => setLocalSelectedIds([])
  }), [localSelectedIds]);

  // Hook de categorías API
  const {
    categories,
    loading,
    fetchCategories
  } = useCategoriesApi();

  /**
   * Efecto para cargar categorías al montar el componente.
   */
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * Efecto para sincronizar estado local con props.
   */
  useEffect(() => {
    setLocalSelectedIds(selectedCategoryIds);
  }, [selectedCategoryIds]);

  /**
   * Maneja el cambio de selección de una categoría.
   *
   * @param {string} categoryId - ID de la categoría
   * @param {boolean} isSelected - Si está seleccionada
   */
  const handleCategoryToggle = useCallback((categoryId, isSelected) => {
    setLocalSelectedIds(isSelected ? [categoryId] : []);
  }, []);

  /**
   * Verifica si una categoría está seleccionada.
   *
   * @param {string} categoryId - ID de la categoría
   * @returns {boolean} Si está seleccionada
   */
  const isCategorySelected = useCallback((categoryId) => localSelectedIds.includes(categoryId), [localSelectedIds]);

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-64">
        <LoadingMC
          loading
          message={t('categoryFilter.loading')}
        />
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-64">
        <span className="tw-text-gray-500 tw-text-sm">
          {t('categoryFilter.empty.title')}
        </span>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      {/* Lista de categorías */}
      <div className="tw-flex-1 tw-overflow-auto tw-py-2">

        {categories.map((category) => {
          const isSelected = isCategorySelected(category.web_id);

          return (
            <CheckBoxMC
              key={category.web_id}
              id={`category-${category.web_id}`}
              name={`category-${category.web_id}`}
              checked={isSelected}
              onChange={(e) => handleCategoryToggle(category.web_id, e.target.checked)}
              className="tw-form-checkbox"
            >
              <span
                htmlFor={`category-${category.web_id}`}
                className="tw-text-sm tw-font-medium tw-text-gray-900 tw-cursor-pointer"
              >
                {category.name}
              </span>
            </CheckBoxMC>
          );
        })}
      </div>
    </div>
  );
}));

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
CategoryFilter.displayName = 'CategoryFilter';

export default CategoryFilter;
